import { useState, useEffect } from "react";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import Button from "../../../../shared/Button";
import { useUpdateJobMutation } from "../../../../redux/services/jobs/jobsApi";
import { toast } from "react-toastify";

/**
 * Notes component for managing job notes in the sidebar
 * @param {Object} props - Component props
 * @param {Object} props.job - The job object
 * @param {Object} props.form - Form state from parent
 * @param {Function} props.onFormChange - Handler to update form state
 */
const Notes = ({ job, form, onFormChange }) => {
  const [updateJob, { isLoading: isSaving }] = useUpdateJobMutation();
  const [localNote, setLocalNote] = useState(form.note || job?.note || "");

  // Sync local state with form/job when they change
  useEffect(() => {
    const noteValue = form.note ?? job?.note ?? "";
    setLocalNote(noteValue);
  }, [job?.note, form.note]);

  /**
   * Handles updating the note value locally and in parent form
   * @param {Event} e - Input change event
   */
  function handleNoteChange(e) {
    const value = e.target.value;
    setLocalNote(value);
    if (onFormChange) {
      onFormChange({ value, id: "note" });
    }
  }

  /**
   * Saves the note to the backend via RTK Query mutation
   */
  async function handleSave() {
    if (!job?._id) {
      toast.error("Unable to save: Job ID not found");
      return;
    }

    try {
      await updateJob({
        id: job._id,
        data: { note: localNote },
      }).unwrap();

      toast.success("Notes saved successfully");
    } catch (error) {
      console.error("Failed to save notes:", error);
      toast.error(error?.data?.message || "Failed to save notes");
    }
  }

  // Get previous notes from job if they exist (array of historical notes)
  const previousNotes = job?.notes || [];

  return (
    <div className="flex flex-col bg-gray-50" style={{ height: "100%" }}>
      {/* Current Note Editor */}
      <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
        <div className="flex-1">
          <textarea
            id="note"
            value={localNote}
            onChange={handleNoteChange}
            placeholder="Add notes about this job..."
            rows={12}
            className="resize-none w-full h-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:outline-none"
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-2 pt-3">
          <Button
            onClick={handleSave}
            type="primary"
            size="sm"
            loading={isSaving}
            disabled={isSaving || !job?._id}
          >
            Save Notes
          </Button>
        </div>
      </div>

      {/* Previous Notes */}
      {previousNotes.length > 0 && (
        <div className="border-t border-gray-200 pt-3 mt-3">
          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <DocumentTextIcon className="h-3.5 w-3.5" />
            Previous Notes ({previousNotes.length})
          </h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {previousNotes.map((note, index) => (
              <div
                key={index}
                className="bg-white p-2.5 rounded-lg text-sm text-gray-700 border border-gray-200"
              >
                {note}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notes;
